// Decorative signal field behind the weekly observation card.
export function initObservation(): void {
  const canvas = document.querySelector<HTMLCanvasElement>("#signalCanvas");
  if (!canvas) return;
  const context = canvas.getContext("2d");
  if (!context) return;

  const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
  let width = 0;
  let height = 0;
  let frame = 0;
  let visible = false;
  let ink = "#3f5149";
  let accent = "#d8f04b";

  function updateColors(): void {
    const style = getComputedStyle(document.body);
    ink = style.getPropertyValue("--ink-soft").trim() || ink;
    accent = style.getPropertyValue("--lime").trim() || accent;
  }

  function draw(time: number): void {
    if (!canvas || !context) return;
    context.clearRect(0, 0, width, height);
    const nodes = Array.from({ length: 15 }, (_, index) => ({
      x: width * (.08 + (index * .071) % .86) + Math.sin(time * .0004 + index * 1.7) * 16,
      y: height * (.13 + (index * .137) % .72) + Math.cos(time * .0005 + index * 1.7) * 12,
    }));
    context.strokeStyle = ink;
    context.lineWidth = 1;
    nodes.forEach((node, index) => {
      for (const other of nodes.slice(index + 1)) {
        const distance = Math.hypot(node.x - other.x, node.y - other.y);
        if (distance >= 170) continue;
        context.globalAlpha = (1 - distance / 170) * .25;
        context.beginPath();
        context.moveTo(node.x, node.y);
        context.lineTo(other.x, other.y);
        context.stroke();
      }
      const signal = index % 5 === 0;
      const pulse = signal ? (Math.sin(time * .002 + index) + 1) * 1.5 : 0;
      context.globalAlpha = signal ? .9 : .5;
      context.fillStyle = signal ? accent : ink;
      context.beginPath();
      context.arc(node.x, node.y, (signal ? 2.4 : 1.25) + pulse, 0, Math.PI * 2);
      context.fill();
    });
    context.globalAlpha = 1;
    frame = requestAnimationFrame(draw);
  }

  function syncMotion(): void {
    cancelAnimationFrame(frame);
    frame = 0;
    if (visible && !document.hidden && !motion.matches && width > 0 && height > 0) {
      frame = requestAnimationFrame(draw);
    }
  }

  new ResizeObserver(() => {
    const rect = canvas.getBoundingClientRect();
    width = rect.width;
    height = rect.height;
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    canvas.width = Math.round(width * ratio);
    canvas.height = Math.round(height * ratio);
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    syncMotion();
  }).observe(canvas);
  new IntersectionObserver(([entry]) => {
    visible = entry.isIntersecting;
    syncMotion();
  }).observe(canvas);
  new MutationObserver(updateColors).observe(document.documentElement, {
    attributes: true, attributeFilter: ["data-theme"],
  });
  motion.addEventListener("change", syncMotion);
  document.addEventListener("visibilitychange", syncMotion);
  updateColors();
}
